import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ecoCardClassName, ecoCardValueClassName } from '../../utils/greenEnergyEcoCard';
import { getActiveSocPriceBand } from '../../utils/socDynamicPriceNotes';
import EmsBatteryIcon from '../EmsBatteryIcon/EmsBatteryIcon';
import '../EmsEcoCardShared.css';
import './EmsBatteryEcoCard.css';

const FONT_STACK_PERCENT_SAFE =
  'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Helvetica, Arial, sans-serif';

function formatTotalBatteryCapacityKwh(capacityKwh) {
  const capacity = Number(capacityKwh);
  if (!Number.isFinite(capacity) || capacity <= 0) return null;
  return Math.round(capacity);
}

export default function EmsBatteryEcoCard({
  socPercent,
  batteryCapacityKwh,
  batteryPowerW,
  iconRef = null,
  socPriceTiers = null,
  openEmsInverterUrl = null,
  showPower = true,
  loading = false,
}) {
  const { t } = useTranslation();
  const batteryKw = batteryPowerW != null ? Number(batteryPowerW) / 1000 : null;
  const batteryDischargeKw = batteryKw != null && batteryKw > 0.05 ? batteryKw : null;
  const batteryCharging = batteryKw != null && batteryKw < -0.05;
  const totalCapacityKwh = useMemo(() => formatTotalBatteryCapacityKwh(batteryCapacityKwh), [batteryCapacityKwh]);
  const socRounded = socPercent != null && Number.isFinite(Number(socPercent)) ? Math.round(Number(socPercent)) : null;
  const batteryDischarging = batteryDischargeKw != null && batteryDischargeKw > 0.05;
  const priceBand = useMemo(() => getActiveSocPriceBand(socPriceTiers, socPercent), [socPriceTiers, socPercent]);
  const valueClassName = priceBand
    ? `green-energy-eco-card__value ems-battery-eco-card__value--band-${priceBand}`
    : ecoCardValueClassName({
        active: batteryDischarging,
        charging: batteryCharging,
      });

  const capacityLabel = totalCapacityKwh != null ? `${totalCapacityKwh} ${t('wt')}` : `— ${t('wt')}`;

  return (
    <div
      className={[
        ecoCardClassName(batteryDischarging),
        'ems-battery-eco-card',
        priceBand ? `ems-battery-eco-card--band-${priceBand}` : '',
        loading ? 'ems-eco-card--loading' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      aria-busy={loading || undefined}
    >
      <span className="ems-eco-card__title ems-battery-eco-card__name-title">{t('bessReserveSource')}</span>
      <div className="ems-battery-eco-card__head">
        <div className="ems-battery-eco-card__battery-stack">
          <span className="ems-battery-eco-card__soc-title">
            {socRounded != null ? socRounded : '—'}
            <span style={{ fontFamily: FONT_STACK_PERCENT_SAFE }}>{' %'}</span>
          </span>
          {showPower && (
            <span className={`${valueClassName} ems-battery-eco-card__power`}>
              {batteryDischargeKw != null
                ? `${batteryDischargeKw.toFixed(2)}${t('powerWt')}`
                : batteryCharging && batteryKw != null
                  ? `${Math.abs(batteryKw).toFixed(2)}${t('powerWt')}`
                  : '—'}
            </span>
          )}
          <div className="ems-battery-eco-card__icon-row">
            <span ref={iconRef} className="green-energy-eco-card__icon ems-battery-eco-card__icon-wrap">
              <EmsBatteryIcon
                socPercent={socPercent}
                charging={batteryCharging}
                socPriceTiers={socPriceTiers}
                size="large"
              />
            </span>
          </div>
          {openEmsInverterUrl && totalCapacityKwh != null ? (
            <a
              className="ems-battery-eco-card__capacity-btn"
              href={openEmsInverterUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={t('openEmsInverterLinkAria', { kwh: totalCapacityKwh })}
            >
              {capacityLabel}
            </a>
          ) : (
            <span className="ems-battery-eco-card__capacity-btn ems-battery-eco-card__capacity-btn--static">
              {capacityLabel}
            </span>
          )}
          <span
            className={[
              'ems-battery-eco-card__status',
              priceBand ? `ems-battery-eco-card__status--band-${priceBand}` : '',
              batteryCharging ? '' : 'ems-battery-eco-card__status--placeholder',
            ]
              .filter(Boolean)
              .join(' ')}
            aria-hidden={!batteryCharging}
          >
            {t('batteryCharging')}
          </span>
        </div>
      </div>
    </div>
  );
}
